/*
Copyright (c) 2012-2026 Grab Taxi Holdings PTE LTD (GRAB), All Rights Reserved.
NOTICE: All information contained herein is, and remains the property of GRAB.
The intellectual and technical concepts contained herein are confidential,
proprietary and controlled by GRAB and may be covered by patents, patents in process,
and are protected by trade secret or copyright law.

You are strictly forbidden to copy, download, store (in any medium), transmit, disseminate,
adapt or change this material in any way unless prior written permission is obtained from GRAB.
Access to the source code contained herein is hereby forbidden to anyone except current GRAB
employees or contractors with binding Confidentiality and Non-disclosure agreements explicitly
covering such access.

The copyright notice above does not evidence any actual or intended publication or disclosure of
this source code, which includes information that is confidential and/or proprietary,
and is a trade secret, of GRAB.

ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC PERFORMANCE, OR PUBLIC DISPLAY OF OR
THROUGH USE OF THIS SOURCE CODE WITHOUT THE EXPRESS WRITTEN CONSENT OF GRAB IS STRICTLY PROHIBITED,
AND IN VIOLATION OF APPLICABLE LAWS AND INTERNATIONAL TREATIES. THE RECEIPT OR POSSESSION OF THIS
SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS TO REPRODUCE,
DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT MAY DESCRIBE,
IN WHOLE OR IN PART.
*/
import React, { FC } from "react"
import { Drawer, notification, UploadFile } from "@duxton-web/ui"
import {
  CameraOutlined,
  ChevronRightUniversal,
  GalleryOutlined,
} from "@duxton-web/icons"

import { useTranslation, useTranslationsMap } from "common/utils/i18n"

import {
  RETURN_MEDIA_MAX_PHOTOS_PER_ITEM,
  RETURN_MEDIA_SOURCE_BY_PICKER,
} from "ecommerce/constants/returnRefund"
import { pickNativePhotoUploadFile } from "ecommerce/utils/nativePhoto"
import { PickImageOptions } from "utils/nativeBridge"

import styles from "./AddPhotosSheet.modules.css"

interface AddPhotosSheetProps {
  open: boolean
  onClose(): void
  /**
   * One picked photo, already in the Upload-list shape — append and render.
   * `mediaSource` is the enum for the row the merchant chose, so the upload can
   * report a camera capture as one instead of flattening it to the default.
   */
  onPhoto(photo: UploadFile, mediaSource: string): void
  /**
   * Brackets a pick: `true` when the app's picker is asked for a photo, `false`
   * once it has answered either way.
   *
   * The caller RESERVES a photo slot for the duration. The sheet closes before the
   * picker appears, so without this the add tile stays live over that gap and a
   * second pick could start against a count the first one has not landed in yet —
   * putting more photos on the item than the cap allows.
   */
  onPickingChange(isPicking: boolean): void
}

/**
 * The webview's "Add photos" bottom sheet: Take photo / Upload photos, each
 * handing off to the app's `pickImage` bridge handler with the chosen source.
 * The app owns permission, picker, and compression, and answers with base64;
 * the sheet converts that to an `UploadFile` and reports it up.
 *
 * Webview-only by design — a desktop browser keeps the plain file input, where
 * the OS picker already offers everything this sheet exists to choose between.
 */
export const AddPhotosSheet: FC<AddPhotosSheetProps> = ({
  open,
  onClose,
  onPhoto,
  onPickingChange,
}) => {
  const text = useTranslationsMap({
    addPhotoFailed: "ecommerce.sellerCentre.returns.addPhotoFailed",
    addPhotosTakePhoto: "ecommerce.sellerCentre.returns.addPhotosTakePhoto",
    addPhotosUploadPhotos:
      "ecommerce.sellerCentre.returns.addPhotosUploadPhotos",
    reviewAddPhotos: "ecommerce.sellerCentre.returns.reviewAddPhotos",
  })
  // Fixed at render time (a module constant), so `useTranslation` can do the
  // `<number>` substitution itself rather than reading the raw template.
  const addPhotosSubtitle = useTranslation(
    "ecommerce.sellerCentre.returns.addPhotosSubtitle",
    { "<number>": String(RETURN_MEDIA_MAX_PHOTOS_PER_ITEM) },
  )

  const handlePick = async (source: PickImageOptions["source"]) => {
    // Close first — the native picker is about to cover the page, and the
    // sheet still being open under it reads as a hang when the pick is slow.
    onClose()
    onPickingChange(true)

    try {
      const photo = await pickNativePhotoUploadFile(source)

      // Null is a cancel / refusal — nothing to add, nothing to report.
      if (photo) {
        onPhoto(photo, RETURN_MEDIA_SOURCE_BY_PICKER[source])
      }
    } catch (error) {
      /*
       * The toast is deliberately generic — a bridge error names an app-side
       * handler the merchant can do nothing with. It goes to the console instead,
       * the way `reportUnavailable` in `notifyNativeHandler` does, so whoever is
       * debugging the webview can see which source failed and why.
       *
       * The MESSAGE, not the error object: the app's rejections carry native stack
       * traces and handler internals, and the console is readable by anything
       * running in the page. The message alone is what identifies the failure.
       */
      // eslint-disable-next-line no-console
      console.warn("AddPhotosSheet: native pick failed", {
        source,
        reason: error instanceof Error ? error.message : String(error),
      })
      notification.error({ message: text.addPhotoFailed })
    } finally {
      onPickingChange(false)
    }
  }

  return (
    <Drawer
      // Own header, like RemoveItemSheet: the Drawer default upper-cases the
      // title and adds a close icon — neither matches the sheet design.
      title={null}
      closable={false}
      placement="bottom"
      height="auto"
      open={open}
      onClose={onClose}
      destroyOnClose
      classNames={{ content: styles.drawerContent }}
    >
      {/* Typography set from exact design values, matching OptionPickerSheet —
          sheet title 20/700, rows 14/600. The Typography components render on
          the page scale, which reads oversized inside a bottom sheet. */}
      <div className={styles.sheetContent}>
        <h2 className={styles.title}>{text.reviewAddPhotos}</h2>
        <p className={styles.subtitle}>{addPhotosSubtitle}</p>
        <button
          type="button"
          className={styles.optionRow}
          onClick={() => handlePick("camera")}
        >
          <span className={styles.optionIcon}>
            <CameraOutlined />
          </span>
          <span className={styles.optionLabel}>{text.addPhotosTakePhoto}</span>
          <span className={styles.chevron}>
            <ChevronRightUniversal />
          </span>
        </button>
        <button
          type="button"
          className={styles.optionRow}
          onClick={() => handlePick("gallery")}
        >
          <span className={styles.optionIcon}>
            <GalleryOutlined />
          </span>
          <span className={styles.optionLabel}>
            {text.addPhotosUploadPhotos}
          </span>
          <span className={styles.chevron}>
            <ChevronRightUniversal />
          </span>
        </button>
      </div>
    </Drawer>
  )
}
